function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

// Branch match IDs are numeric/alphanumeric identifiers — reject anything else.
function sanitizeMatchId(value: string): string | undefined {
  const cleaned = value.replace(/[^a-zA-Z0-9\-_]/g, "").slice(0, 128);
  return cleaned || undefined;
}

// Branch referrers are URL-encoded strings; strip HTML-injection chars, dangerous URI schemes, and cap length.
function sanitizeReferrer(value: string): string | undefined {
  const cleaned = value
    .trim()
    .replace(/[<>"'`\r\n\t\f\v]/g, "")
    .replace(/^(javascript|data|vbscript):/i, "")
    .slice(0, 2048);
  return cleaned || undefined;
}

type BranchSDK = {
  init?: (
    key: string,
    options?: Record<string, unknown>,
    callback?: (err: Error | null, data: Record<string, unknown>) => void
  ) => void;
  data?: (callback: (err: Error | null, data: Record<string, unknown>) => void) => void;
  setRequestMetadata?: (key: string, value: string) => void;
  setAPIUrl?: (url: string) => void;
  track?: (event: string, metadata?: Record<string, unknown>) => void;
  logEvent?: (
    event: string,
    eventData?: Record<string, unknown>,
    contentItems?: unknown[] | undefined,
    customerEventAlias?: string,
    success?: () => void,
    error?: (err: unknown) => void
  ) => void;
};

export type BranchSession = {
  fromBranch: boolean;
  matchId?: string;
  referrer?: string;
  completeRegistrationTracked?: boolean;
};

const STORAGE_KEY = "branch-session";
const DEFAULT_SESSION: BranchSession = { fromBranch: false };

export class BranchManager {
  private branch?: BranchSDK;
  private session: BranchSession = DEFAULT_SESSION;
  private initialized = false;
  private pendingEvents: { event: string; payload: Record<string, unknown> }[] = [];

  async init(branchKey?: string): Promise<void> {
    this.detectFromUrl(window);
    const key = this.resolveBranchKey(branchKey);
    await this.ensureSdkLoaded();
    const branch = (window as unknown as { branch?: BranchSDK }).branch;
    if (!branch?.init) {
      return;
    }

    this.branch = branch;
    if ((window as unknown as { __branchEarlyInitDone__?: boolean }).__branchEarlyInitDone__) {
      this.initialized = true;
      this.adoptBranchDataSession();
      this.flushPending();
      return;
    }
    if (!key) {
      return;
    }
    this.configureSdk(branch);
    const opts = this.buildInitOptions();
    branch.init(key, opts, (err) => {
      if (err) {
        return;
      }
      this.initialized = true;
      this.detectFromUrl(window);
      this.adoptBranchDataSession();
      this.flushPending();
    });
  }

  private resolveBranchKey(explicit?: string): string | undefined {
    if (explicit) return explicit;
    try {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.get("branch_key");
      if (fromUrl) return fromUrl;
    } catch {
      /* noop */
    }
  const env = this.getEnv();
  return env.BRANCH_KEY;
  }

  private async ensureSdkLoaded(): Promise<void> {
    const exists = !!document.querySelector('script[src*="cdn.branch.io/branch-latest.min.js"]');
  if (exists) { return; }
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdn.branch.io/branch-latest.min.js";
      script.async = true;
      try { script.dataset.cookieconsent = "marketing"; } catch { /* noop */ }
      script.onload = () => resolve();
  script.onerror = () => reject(new Error("Failed to load Branch SDK script"));
      document.body.appendChild(script);
    });
  }

  private buildInitOptions(): Record<string, unknown> {
    const opts: Record<string, unknown> = {};
    try {
      const url = new URL(window.location.href);
      const mid = url.searchParams.get("_branch_match_id");
      if (mid) opts.branch_match_id = mid;
    } catch {
      /* noop */
    }
    return opts;
  }

  private configureSdk(branch: BranchSDK): void {
    try {
      const url = new URL(window.location.href);
      const mid = url.searchParams.get("_branch_match_id");
      if (mid) branch.setRequestMetadata?.("branch_match_id", mid);
      const env = this.getEnv();
      branch.setRequestMetadata?.("environment", env.ENVIRONMENT || "dev");
      branch.setRequestMetadata?.("platform", "web");
      const apiUrl = url.searchParams.get("branch_api_url") || env.BRANCH_API_URL;
      if (apiUrl) branch.setAPIUrl?.(apiUrl);
    } catch {
      /* noop */
    }
  }

  private getEnv(): { ENVIRONMENT?: string; BRANCH_API_URL?: string; BRANCH_KEY?: string } {
    try {
      const w = window as unknown as { process?: { env?: { [k: string]: string } } };
      return (w.process?.env as { [k: string]: string } | undefined) ?? {};
    } catch {
      return {};
    }
  }

  detectFromUrl(windowObj: Window): void {
    const parsed = this.parseBranchParams(windowObj);
    if (parsed) {
      this.session = {
        fromBranch: true,
        matchId: parsed.matchId,
        referrer: parsed.referrer,
        completeRegistrationTracked: false,
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session)); } catch { /* noop */ }
      return;
    }
    const restored = this.restoreSessionFromStorage();
    if (restored) {
      this.session = restored;
    }
  }

  private parseBranchParams(windowObj: Window): { matchId?: string; referrer?: string } | null {
    try {
      const url = new URL(windowObj.location.href);
      const rawMatchId = url.searchParams.get("_branch_match_id") ?? undefined;
      const rawReferrer = url.searchParams.get("_branch_referrer") ?? undefined;
      const matchId = rawMatchId ? sanitizeMatchId(rawMatchId) : undefined;
      const referrer = rawReferrer ? sanitizeReferrer(rawReferrer) : undefined;
      if (matchId || referrer) return { matchId, referrer };
    } catch {
      /* noop */
    }
    return null;
  }

  private restoreSessionFromStorage(): BranchSession | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return null;
      const raw: unknown = JSON.parse(saved);
      if (!isObject(raw)) return null;
      const fromBranch = typeof raw.fromBranch === "boolean" ? raw.fromBranch : false;
      const matchId = typeof raw.matchId === "string" ? raw.matchId : undefined;
      const referrer = typeof raw.referrer === "string" ? raw.referrer : undefined;
      const complete = typeof raw.completeRegistrationTracked === "boolean" ? raw.completeRegistrationTracked : false;
      return { fromBranch, matchId, referrer, completeRegistrationTracked: complete };
    } catch {
      return null;
    }
  }

  getSession(): BranchSession {
    return this.session;
  }

  clearSession(): void {
    this.session = DEFAULT_SESSION;
    localStorage.removeItem(STORAGE_KEY);
  }

  trackCompleteRegistration(extra?: Record<string, unknown>): void {
    if (!this.session.fromBranch) {
      return;
    }

    if (this.session.completeRegistrationTracked) {
      return;
    }

    const payload = {
      ...(this.session.matchId ? { _branch_match_id: this.session.matchId } : {}),
      ...(this.session.referrer ? { _branch_referrer: this.session.referrer } : {}),
      ...(extra ?? {}),
      tags: ["COMPLETE_REGISTRATION"],
    } as Record<string, unknown>;

    try {
      this.sendEvent("COMPLETE_REGISTRATION", payload);

      this.session.completeRegistrationTracked = true;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session));
    } catch (err) {
  /* noop */
    }
  }

  private sendEvent(event: string, payload: Record<string, unknown>): void {
    if (this.queueOrAdopt(event, payload)) {
      return;
    }
    this.sendViaSdk(event, payload);
  }

  private queueOrAdopt(event: string, payload: Record<string, unknown>): boolean {
    if (this.isReady()) return false;
    this.adoptGlobalBranchIfAny();
    if (this.isReady()) return false;
    this.queueOnce(event, payload);
    return true;
  }

  private isReady(): boolean {
    return !!this.branch && this.initialized;
  }

  private adoptGlobalBranchIfAny(): void {
    const maybeBranch = (window as unknown as { branch?: BranchSDK }).branch;
    if (!maybeBranch) return;
  this.branch ??= maybeBranch;
    if (!this.initialized) this.initialized = true;
  }

  private queueOnce(event: string, payload: Record<string, unknown>): void {
    const serialized = JSON.stringify(payload);
    const alreadyQueued = this.pendingEvents.some(
      (e) => e.event === event && JSON.stringify(e.payload) === serialized
    );
    if (alreadyQueued) {
      return;
    }
    this.pendingEvents.push({ event, payload });
  }

  private sendViaSdk(event: string, payload: Record<string, unknown>): void {
    const sdk = this.branch;
    if (!sdk) {
      return;
    }
    if (sdk.logEvent) {
      const alias = event === "COMPLETE_REGISTRATION" ? "completeRegistration" : undefined;
      sdk.logEvent(event, payload, undefined, alias);
      return;
    }
    if (sdk.track) {
      if (event === "COMPLETE_REGISTRATION") {
        sdk.track("completeRegistration", { ...payload, customer_event_alias: "completeRegistration" });
      } else {
        sdk.track(event, payload);
      }
    }
  }

  private flushPending(): void {
  if (!this.branch || !this.initialized || this.pendingEvents.length === 0) { return; }
    const queued = [...this.pendingEvents];
    this.pendingEvents = [];
    queued.forEach(({ event, payload }) => this.sendEvent(event, payload));
  }

  private adoptBranchDataSession(): void {
  const sdk = this.branch;
  if (!sdk?.data) { return; }
    try {
      sdk.data(this.onBranchDataBound);
    } catch {
      /* noop */
    }
  }

  private readonly onBranchDataBound = (err: Error | null, data: Record<string, unknown>): void => {
    this.onBranchData(err, data);
  };

  private onBranchData(err: Error | null, data: unknown): void {
    if (err || !isObject(data)) return;
    const clicked = this.extractClickedBranchLink(data);
    if (!clicked || this.session.fromBranch) return;
    this.session.fromBranch = true;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session)); } catch { /* noop */ }
  }

  private extractClickedBranchLink(data: Record<string, unknown>): boolean {
    const raw = data["+clicked_branch_link"];
    return typeof raw === "boolean" ? raw : false;
  }
}

export const branchManager = new BranchManager();
